// Éléments des cartes : tout ce qui se dessine sur une carte (terrains et objets posés) est un élément
// défini dans l'éditeur d'éléments. Partagé par le serveur (collisions, interactions), le jeu (rendu,
// prédiction des déplacements) et l'administration.
import type { Footstep } from './footsteps.ts';
import { DIRECTION_VECTORS, type Direction, type Position } from './protocol.ts';
import type { LightSource } from './lighting.ts';
import type { ObjectMotion } from './object-motion.ts';
import type { ObjectTrip } from './vehicles.ts';
import type { ObjectDrop } from './drops.ts';
import type { ItemId } from './items.ts';
import type { Interaction } from './tiles.ts';
import type { ObjectMechanism } from './mechanisms.ts';
import { reachedStep, type QuestProgress } from './quests.ts';

/** Un morceau d'image : une découpe de tileset (coin supérieur gauche en pixels). */
export interface SpriteRef {
  image: string;
  x: number;
  y: number;
}

/** Animation : l'image k est décalée de (k·dx, k·dy) pixels dans le tileset. */
export interface ElementAnimation {
  frames: number;
  dx: number;
  dy: number;
  /** Durée d'une image, en millisecondes. */
  ms: number;
}

/**
 * Pièces d'un raccord « blob », découpées en quarts : `center` (entouré de semblables), bords
 * (voisin différent au nord, au sud…), coins extérieurs (deux voisins différents) et coins intérieurs
 * (`ine` : voisins nord et est semblables mais pas le diagonal nord-est). Une pièce absente ne dessine
 * rien (ex. le centre de l'eau, laissé aux vagues animées d'un calque inférieur).
 */
export const BLOB_PIECES = ['nw', 'n', 'ne', 'w', 'center', 'e', 'sw', 's', 'se', 'inw', 'ine', 'isw', 'ise'] as const;
export type BlobPiece = (typeof BLOB_PIECES)[number];

/** Morceaux d'un raccord en ligne : début (haut ou gauche), milieu, fin (bas ou droite), et case seule. */
export const STRIP_PIECES = ['start', 'middle', 'end', 'single'] as const;
export type StripPiece = (typeof STRIP_PIECES)[number];
/** Sens d'un raccord en ligne : une échelle est verticale, un pont horizontal. */
export type StripAxis = 'vertical' | 'horizontal';
export const isStripAxis = (value: unknown): value is StripAxis => value === 'vertical' || value === 'horizontal';

/** Angles d'une barrière, nommés d'après leur place dans un enclos : `nw` ┌, `ne` ┐, `sw` └, `se` ┘. */
export const LINE_CORNERS = ['nw', 'ne', 'sw', 'se'] as const;
export type LineCorner = (typeof LINE_CORNERS)[number];

export type LayerShape =
  /** Une image ; plusieurs : elles s'enchaînent dans l'ordre (animation image par image, voir `frameMs`). */
  | { mode: 'single'; sprites: SpriteRef[] }
  /** Une variante tirée au hasard (mais toujours la même) pour chaque case. */
  | { mode: 'variants'; variants: { sprite: SpriteRef; weight: number }[] }
  | { mode: 'blob'; pieces: Partial<Record<BlobPiece, SpriteRef>> }
  /**
   * Barrières, haies, ponts : poteau seul, segment horizontal ou vertical, ou angle, selon les voisins (voir
   * `linePiece`). Croisements, T et angles sans image : segments horizontal et vertical superposés.
   */
  | { mode: 'lines'; single: SpriteRef | null; horizontal: SpriteRef | null; vertical: SpriteRef | null; corners: Record<LineCorner, SpriteRef | null> }
  /**
   * Bordure (falaise, rebord, à sauter ou non) : une image par sens, choisi pour chaque case en peignant la carte
   * (côtés et angles ; voir MapBorder). Un angle est dessiné en coin intérieur (`inner-…`) quand ses voisins vers ses
   * deux sens sont des bordures.
   */
  | { mode: 'border'; sprites: Record<BorderSprite, SpriteRef | null> }
  /**
   * Raccord en ligne (échelle, pont) : ne se raccorde qu'avec ses voisins dans son sens, en haut et en bas ou à gauche
   * et à droite (voir `stripPiece`) ; un morceau vide reprend le milieu.
   */
  | { mode: 'strip'; axis: StripAxis; pieces: Record<StripPiece, SpriteRef | null> }
  /**
   * Objet mobile : une animation par sens de déplacement (images qui s'enchaînent, voir `frameMs`). Un sens sans image
   * reprend celles d'un autre (le bas d'abord). Immobile, l'objet montre son sens de départ.
   */
  | { mode: 'directional'; sprites: Record<Direction, SpriteRef[]> }
  /**
   * Trace (terrain) : pas dans le sable, sillage dans l'eau. Rien au repos ; chaque case où passe un personnage reçoit
   * une marque qui suit son trajet et s'efface en `fadeMs`. `straight` est dessinée pour un passage vers le bas, `turn`
   * pour un virage (arrivé par le haut, reparti vers la droite) : elles sont pivotées et retournées pour les autres sens
   * (voir trailPiece) ; un virage sans image reprend la ligne droite.
   */
  | { mode: 'trail'; straight: SpriteRef | null; turn: SpriteRef | null; fadeMs: number };

export type LayerMode = LayerShape['mode'];

/** Sens repris par un calque « par sens » quand le sien n'a pas d'image. */
const DIRECTION_FALLBACK: readonly Direction[] = ['down', 'right', 'left', 'up'];

/** Images d'un calque « par sens » pour ce sens (ou celles d'un autre sens, s'il n'en a pas). */
export function directionalFrames(sprites: Readonly<Record<Direction, SpriteRef[]>>, dir: Direction): SpriteRef[] {
  if (sprites[dir].length) return sprites[dir];
  return DIRECTION_FALLBACK.map((other) => sprites[other]).find((frames) => frames.length) ?? [];
}

/** Durée d'effacement d'une trace, en ms. */
export const TRAIL_FADE_LIMITS = { default: 1500, min: 100, max: 60_000 } as const;

/** L'élément laisse une trace (calque « Trace ») là où l'on passe. */
export const hasTrail = (element: Pick<MapElement, 'layers'>): boolean => element.layers.some((layer) => layer.shape.mode === 'trail');

/** Quarts de tour (sens des aiguilles d'une montre) qui amènent le bas sur chaque sens. */
const QUARTERS_FROM_DOWN: Readonly<Record<Direction, number>> = { down: 0, left: 1, up: 2, right: 3 };
const OPPOSITE: Readonly<Record<Direction, Direction>> = { down: 'up', up: 'down', left: 'right', right: 'left' };
/** Où mène le virage dessiné (vers la droite) une fois pivoté de 0, 1, 2 ou 3 quarts de tour. */
const TURN_EXITS: readonly Direction[] = ['right', 'down', 'left', 'up'];

/**
 * Image d'une marque de trace pour un passage arrivé vers `entry` et reparti vers `exit` (null : case de départ, ou
 * personnage encore dessus) : ligne droite ou virage, pivoté de `quarters` quarts de tour et retourné (`mirror`, avant
 * de pivoter) pour un virage de l'autre côté. Un demi-tour est tracé tout droit.
 */
export function trailPiece(entry: Direction | null, exit: Direction | null): { piece: 'straight' | 'turn'; quarters: number; mirror: boolean } {
  const into = entry ?? exit ?? 'down', out = exit ?? into;
  const quarters = QUARTERS_FROM_DOWN[into];
  if (out === into || out === OPPOSITE[into]) return { piece: 'straight', quarters, mirror: false };
  return { piece: 'turn', quarters, mirror: out !== TURN_EXITS[quarters] };
}

/** L'élément a un calque « par sens » : son image suit le sens de l'objet, sans être retournée ni pivotée. */
export const isDirectional = (element: Pick<MapElement, 'layers'>): boolean => element.layers.some((layer) => layer.shape.mode === 'directional');

/**
 * Lecture des images d'un calque : `loop` en boucle ; `trigger` au déclenchement, une seule fois : une porte
 * reste sur sa première image, s'ouvre jusqu'à la dernière quand un personnage s'approche (voir `triggerCells`) et
 * se referme en sens inverse quand il s'éloigne, chaque sens dans sa durée (voir `triggerOpenMs`, `triggerCloseMs`) ;
 * `use` à l'utilisation : invisible au repos, les images s'enchaînent une fois quand le joueur se sert de l'objet
 * (borne de soin, PC, caisse de boutique ; objets seulement) ; `depart` au départ d'un véhicule : invisible à quai,
 * les images s'enchaînent une fois quand le joueur part avec, la dernière restant affichée (véhicules seulement) ;
 * `departLoop` de même, mais les images tournent en boucle tout le trajet (une hélice, des roues) sans retarder le départ.
 * Hors boucle, `idleFirstFrame` montre la première image au repos au lieu de rien.
 */
export const LAYER_PLAYBACKS = {
  loop: 'En boucle', trigger: 'Au déclenchement (joueur proche)', use: 'À l\'utilisation (une fois, quand on s\'en sert)',
  depart: 'Au départ (véhicule : une fois, quand le joueur part avec)',
  departLoop: 'Au départ (véhicule : en boucle, quand le joueur part avec)',
} as const;
export type LayerPlayback = keyof typeof LAYER_PLAYBACKS;
export const isLayerPlayback = (value: unknown): value is LayerPlayback => typeof value === 'string' && Object.hasOwn(LAYER_PLAYBACKS, value);

/** Rayon de déclenchement d'un élément, en cases autour de ses cases de déclenchement (0 : seulement sur l'une d'elles). */
export const TRIGGER_RADIUS_LIMITS = { default: 1, max: 10 } as const;

/**
 * Un personnage en (x, y) est-il assez près de l'élément (coin en `at`, tourné de `turns` quarts de tour) pour le
 * déclencher ? Le rayon se compte depuis chacune de ses cases de déclenchement (voir `triggerCells`), diagonales comprises.
 */
export function withinTrigger(at: Pick<MapObject, 'x' | 'y' | 'turns'>, element: Pick<MapElement, 'width' | 'height' | 'triggerRadius' | 'triggerCells'>, { x, y }: { x: number; y: number }): boolean {
  for (const covered of coveredCells(at, element)) {
    if (element.triggerCells[covered.cell] && Math.max(Math.abs(covered.x - x), Math.abs(covered.y - y)) <= element.triggerRadius) return true;
  }
  return false;
}

/** Durées de l'aller (ouverture) et du retour (fermeture) d'un élément à déclenchement, en ms. */
export const TRIGGER_MS_LIMITS = { default: 400, min: 20, max: 10_000 } as const;

/** L'élément a-t-il un calque à déclenchement ? */
export const hasTrigger = (element: Pick<MapElement, 'layers'>): boolean => element.layers.some((layer) => layer.playback === 'trigger');

/** Durée d'un calque joué à l'utilisation : toutes ses images, une fois (0 pour un calque à une seule image). */
export function useDuration(layer: Pick<ElementLayer, 'shape' | 'animation' | 'frameMs'>): number {
  if (layer.animation) return layer.animation.frames * layer.animation.ms;
  return layer.shape.mode === 'single' ? layer.shape.sprites.length * layer.frameMs : layer.frameMs;
}

/** Plus longue animation « à l'utilisation » de l'élément (0 s'il n'en a pas). */
export const useDurationOf = (element: Pick<MapElement, 'layers'>): number =>
  Math.max(0, ...element.layers.filter((layer) => layer.playback === 'use').map(useDuration));

/** Délai avant une lecture « au départ », en ms. */
export const DEPART_DELAY_LIMITS = { max: 10_000 } as const;

/**
 * Plus longue animation « au départ » (une fois) d'un véhicule, délai compris (0 s'il n'en a pas) : il ne se met en
 * route qu'après. Celles en boucle ne le retiennent pas.
 */
export const departDurationOf = (element: Pick<MapElement, 'layers'>): number =>
  Math.max(0, ...element.layers.filter((layer) => layer.playback === 'depart').map((layer) => layer.departDelayMs + useDuration(layer)));

/**
 * Ouverture (0 : fermé, 1 : ouvert) d'un élément à déclenchement `elapsed` ms plus tard : elle avance à la vitesse de
 * l'aller tant qu'un personnage est proche (`near`), recule à celle du retour sinon, d'où qu'elle en soit.
 */
export function stepOpening(opening: number, element: Pick<MapElement, 'triggerOpenMs' | 'triggerCloseMs'>, near: boolean, elapsed: number): number {
  return Math.min(1, Math.max(0, opening + (near ? elapsed / element.triggerOpenMs : -elapsed / element.triggerCloseMs)));
}

/** Image montrée par un calque à déclenchement de `count` images, à l'ouverture `opening` (la dernière : tout ouvert). */
export const triggerFrame = (count: number, opening: number): number => Math.min(count - 1, Math.floor(opening * (count - 1) + 1e-9));

export interface ElementLayer {
  shape: LayerShape;
  /** Animation par bande : chaque image est décalée de (dx, dy) dans le tileset. */
  animation: ElementAnimation | null;
  /** Durée de chaque image quand une « image unique » en compte plusieurs, en millisecondes. */
  frameMs: number;
  /** Les images tournent en boucle, ou sont jouées au déclenchement (voir LayerPlayback). */
  playback: LayerPlayback;
  /** Chaque case démarre l'animation sur une image différente (toujours la même pour une case donnée). */
  randomStart: boolean;
  /**
   * Lecture autre qu'en boucle (déclenchement, utilisation, départ) : la première image reste affichée au repos (porte
   * fermée, véhicule à quai, et pendant le délai du départ), sinon le calque n'apparaît que quand ses images s'enchaînent.
   */
  idleFirstFrame: boolean;
  /** Lecture « au départ » : attente avant que le calque apparaisse et que ses images s'enchaînent, en ms (0 : tout de suite). */
  departDelayMs: number;
  /** Taille de la découpe, en pixels du tileset (par défaut, celle de l'élément). */
  width: number;
  height: number;
  /** Décalage du dessin par rapport au coin de l'élément, en pixels du tileset (un arbre déborde vers le haut). */
  offsetX: number;
  offsetY: number;
  /**
   * Dessiné avec les personnages, trié par ligne : on passe derrière un arbre ou un toit, devant une façade. Toujours
   * vrai pour un objet (sa profondeur par case dit ce qui passe devant, derrière ou est trié), toujours faux pour un
   * terrain (à plat, sous les objets et les personnages).
   */
  sorted: boolean;
}

export type ElementKind = 'terrain' | 'object';

/**
 * Place, par rapport aux personnages, des calques « passe devant / derrière » sur une case de l'élément :
 * `sort` trié selon la ligne du bas de l'élément (on passe derrière en étant au-dessus), `front` toujours
 * par-dessus les personnages, `behind` toujours dessous. Ce qui déborde de l'élément suit la case du bord.
 */
export type CellDepth = 'sort' | 'front' | 'behind';
export const CELL_DEPTHS: readonly CellDepth[] = ['sort', 'front', 'behind'];
export const isCellDepth = (value: unknown): value is CellDepth => typeof value === 'string' && (CELL_DEPTHS as readonly string[]).includes(value);

export interface MapElementInput {
  /** Identifiant stable des éléments de base (utilisé par le contenu de démo), null sinon. */
  key: string | null;
  name: string;
  /** `terrain` : une case, peint dans un calque de terrain ; `object` : posé librement, sur plusieurs cases. */
  kind: ElementKind;
  /** Taille d'une case dans les images sources, en pixels. */
  gridSize: number;
  /** Taille en cases (1×1 pour un terrain). */
  width: number;
  height: number;
  /** Groupe de raccord : les voisins d'un même groupe se raccordent. */
  group: string;
  /** Autres groupes considérés comme semblables pour les raccords (ex. l'eau et le pont). */
  connectsWith: string[];
  /** Si non vide : seuls ces groupes sont considérés comme différents (un chemin ne borde que l'herbe). */
  bordersOnly: string[];
  /**
   * Objet : terrain dessiné sous chaque case de l'objet ; toujours null pour un terrain (ce qui passe dessous se
   * peint sur un calque de terrain inférieur).
   */
  underlay: number | null;
  layers: ElementLayer[];
  /** Hitbox : une valeur par case, ligne par ligne ; vrai = bloquant. */
  collision: boolean[];
  /** Profondeur des calques « passe devant / derrière », une valeur par case, ligne par ligne. */
  depth: CellDepth[];
  interaction: Interaction | null;
  /**
   * Interaction possible (objet) : chaque objet posé de cet élément peut être visé par un objectif de quête « interagir
   * avec un objet » ; pendant cette quête, le joueur, face à lui, l'examine. Hors de cette quête, rien ne se passe.
   */
  inspectable: boolean;
  /** Masque le bas des personnages qui s'y tiennent (hautes herbes). */
  coversCharacters: boolean;
  /** Bruit des pas du joueur qui marche dessus (voir footsteps.ts) ; null : aucun. */
  footstep: Footstep | null;
  /**
   * Bordure à sauter (terrain) : chaque case peinte a un sens, choisi au pinceau (voir MapBorder). Elle bloque le
   * passage, sauf en avançant dans ce sens : on saute alors par-dessus et on atterrit sur la case d'après.
   */
  ledge: boolean;
  /** Objet mobile : chaque objet posé peut suivre un chemin, tracé en le posant sur la carte (voir ObjectMotion). */
  moving: boolean;
  /**
   * Véhicule (objet, jamais mobile en même temps) : le joueur qui marche sur une de ses cases d'embarquement part avec
   * lui ; chemin, question, durée et destination se règlent sur chaque objet posé (voir ObjectTrip).
   */
  vehicle: boolean;
  /** Véhicule où le joueur disparaît (monté dans un wagon…) dès que le véhicule se met en route ; faux pour les autres éléments. */
  hidesRider: boolean;
  /** Cases d'embarquement d'un véhicule, une valeur par case, ligne par ligne (toutes fausses pour les autres éléments). */
  boardCells: boolean[];
  /** Lumière émise par l'élément (par chaque case peinte, pour un terrain) ; null : aucune. */
  light: LightSource | null;
  /**
   * Cases d'où part la lumière, une valeur par case, ligne par ligne : chaque groupe de cases cochées qui se touchent
   * éclaire depuis son centre (tout l'objet : une lumière au milieu ; le haut d'un lampadaire : sa lanterne).
   */
  lightCells: boolean[];
  /**
   * Obstacle franchissable avec la capacité du même nom (conditions réglées dans l'onglet Configuration) : objet à
   * dégager (buisson à couper…) ou à pousser (rocher), terrain où l'on surfe (eau) ; null : aucun.
   */
  obstacle: ObstacleKind | null;
  /**
   * Calques à déclenchement (porte) : ils s'ouvrent quand un personnage se tient à au plus tant de cases de l'une des
   * cases de déclenchement (voir `triggerCells`), diagonales comprises.
   */
  triggerRadius: number;
  /**
   * Cases de déclenchement, une valeur par case, ligne par ligne : le rayon se compte depuis elles (la case de la porte
   * d'un grand bâtiment). Toujours la seule case d'un terrain.
   */
  triggerCells: boolean[];
  /**
   * Calques à déclenchement : durée de l'aller, de la première image à la dernière quand un personnage approche, et du
   * retour, en sens inverse quand il s'éloigne (des traces de pas : 100 ms à l'aller, 800 ms au retour), en ms.
   */
  triggerOpenMs: number;
  triggerCloseMs: number;
}

/**
 * Ce que le joueur fait d'un obstacle :
 * - `clear` (objet) : face à lui, la touche d'action le fait disparaître, pour de bon et pour ce joueur seul ;
 * - `push` (objet) : en marchant contre lui, il avance d'une case si elle est libre ; il revient à sa place quand le
 *   joueur quitte la carte ;
 * - `surf` (terrain bloquant, l'eau) : face à lui, la touche d'action fait monter le joueur sur {un monstre} de son
 *   équipe, avec lequel il avance sur ce terrain jusqu'à reprendre pied.
 */
export type ObstacleKind = 'clear' | 'push' | 'surf';
export const OBSTACLE_KINDS: Readonly<Record<ObstacleKind, { label: string }>> = {
  clear: { label: 'À couper (buisson…)' },
  push: { label: 'À pousser (rocher…)' },
  surf: { label: 'Surfable (eau)' },
};
/** Genres possibles selon le type d'élément : un terrain ne se dégage ni ne se pousse, on ne surfe pas sur un objet. */
export const OBSTACLE_KINDS_FOR: Readonly<Record<ElementKind, readonly ObstacleKind[]>> = { object: ['clear', 'push'], terrain: ['surf'] };
export const isObstacleKind = (value: unknown): value is ObstacleKind => typeof value === 'string' && Object.hasOwn(OBSTACLE_KINDS, value);

/**
 * Conditions d'une capacité (couper, pousser, surfer, voler) : badge, {un monstre} du type dans l'équipe, objet dans
 * le sac (null : aucune).
 */
export interface Requirements {
  badgeId: number | null;
  element: string | null;
  item: string | null;
}

export interface MapElement extends MapElementInput {
  id: number;
}

/** Angles d'une bordure, nommés d'après leurs deux sens (`down-right` : coin sud-est). */
export const BORDER_CORNERS = ['down-right', 'down-left', 'up-left', 'up-right'] as const;
export type BorderCorner = (typeof BORDER_CORNERS)[number];
/**
 * Sens d'une case de bordure : un côté ou un angle (ses deux côtés). Une bordure à sauter se saute dans ce sens (un
 * angle, dans ses deux sens).
 */
export type BorderDirection = Direction | BorderCorner;
/** Dans le sens des aiguilles d'une montre, en partant du bas (ordre de la touche R). */
export const BORDER_DIRECTIONS: readonly BorderDirection[] = ['down', 'down-left', 'left', 'up-left', 'up', 'up-right', 'right', 'down-right'];
export const isBorderDirection = (value: unknown): value is BorderDirection => typeof value === 'string' && (BORDER_DIRECTIONS as readonly string[]).includes(value);
/** Images d'une bordure : une par sens, plus les coins intérieurs. */
export type BorderSprite = BorderDirection | `inner-${BorderCorner}`;
export const BORDER_SPRITES: readonly BorderSprite[] = [...BORDER_DIRECTIONS, ...BORDER_CORNERS.map((corner) => `inner-${corner}` as const)];

/** Côtés d'un sens de bordure (deux pour un angle) : ceux vers lesquels une bordure à sauter se saute. */
export const borderSides = (dir: BorderDirection): readonly Direction[] => dir.split('-') as Direction[];

/** Terrain dont les cases ont un sens de bordure : avec un calque Bordure, ou à sauter. */
export const hasBorder = (element: Pick<MapElement, 'layers' | 'ledge'>): boolean => element.ledge || element.layers.some((layer) => layer.shape.mode === 'border');

/** Sens d'une case de bordure (voir hasBorder) ; une case sans entrée a le sens par défaut (vers le bas). */
export interface MapBorder {
  x: number;
  y: number;
  dir: BorderDirection;
}

export const DEFAULT_BORDER_DIRECTION: BorderDirection = 'down';

/**
 * Rotation d'un élément posé, en quarts de tour dans le sens des aiguilles d'une montre (0 à 3) : l'image tourne autour
 * du centre de ses cases, et sa hitbox, sa profondeur et ses lumières avec elle. En plus, MIRROR (4) retourne l'élément
 * (objet ou terrain) gauche-droite (voir isMirrored).
 */
export type Turns = 0 | 1 | 2 | 3 | 4;
export const MIRROR: Turns = 4;
/** Rotations proposées, dans l'ordre où R les parcourt. */
export const TURN_CHOICES: readonly Turns[] = [0, 1, 2, 3, MIRROR];
export const isTurns = (value: unknown): value is Turns => TURN_CHOICES.includes(value as Turns);

/**
 * De combien de quarts de tour l'élément tourne à chaque appui sur R : 0 s'il ne tourne pas (calques autres qu'une image
 * unique, des variantes ou une trace, bordure, objet mobile), 2 pour un objet qui n'est pas carré (il garde ses cases :
 * demi-tour et miroir seulement), 1 sinon.
 */
export function turnStep(element: Pick<MapElement, 'layers' | 'ledge' | 'moving' | 'vehicle' | 'width' | 'height'>): 0 | 1 | 2 {
  // Une bordure s'oriente par son sens, pas par la rotation de la case.
  // Une trace suit le trajet des personnages, pas la rotation de la case.
  if (element.ledge || element.moving || element.vehicle || !element.layers.every((layer) => ['single', 'variants', 'trail'].includes(layer.shape.mode))) return 0;
  return element.width === element.height ? 1 : 2;
}

/** Un élément (objet ou terrain) posé en miroir est retourné gauche-droite, pas tourné. */
export const isMirrored = (turns: Turns): boolean => turns === MIRROR;

/** Rotations que l'élément permet (voir turnStep), dans l'ordre de TURN_CHOICES. */
export function turnChoices(element: Parameters<typeof turnStep>[0]): Turns[] {
  const step = turnStep(element);
  return step ? TURN_CHOICES.filter((turns) => turns % step === 0) : [0];
}

/** Rotation tenue par l'élément : celle demandée, ramenée à ce qu'il permet (voir turnStep). */
export function allowedTurns(element: Parameters<typeof turnStep>[0], turns: Turns): Turns {
  const step = turnStep(element);
  return step === 0 ? 0 : (turns - (turns % step)) as Turns;
}

/** Case de terrain tournée (calque 0 : le sol) ; une case sans entrée n'est pas tournée. */
export interface MapRotation {
  layer: number;
  x: number;
  y: number;
  turns: Turns;
}

/**
 * Marge (en cases) autour de la carte où l'éditeur peut peindre du terrain et poser des objets. Le jeu ne la montre
 * pas : elle sert aux raccords au bord de la carte et aux objets coupés par le bord (ce qui en déborde dans la carte
 * reste visible).
 */
export const MAP_MARGIN = 4;

/** Terrain d'une case de la marge (hors de la carte), sur un calque. */
export interface MapOuterTile {
  layer: number;
  x: number;
  y: number;
  tileId: number;
  turns: Turns;
}

/** La case est-elle dans la carte ou dans sa marge ? */
export const inMargin = (map: Pick<MapLayout, 'width' | 'height'>, x: number, y: number): boolean =>
  x >= -MAP_MARGIN && y >= -MAP_MARGIN && x < map.width + MAP_MARGIN && y < map.height + MAP_MARGIN;

/**
 * Places possibles du coin supérieur gauche d'un objet (ou d'une étape de son chemin) : il suffit qu'une de ses cases
 * touche la carte ou sa marge. Un objet long (un tram) peut ainsi attendre tout entier hors champ.
 */
export function objectRange(map: Pick<MapLayout, 'width' | 'height'>, { width, height }: Pick<MapElement, 'width' | 'height'>): { minX: number; maxX: number; minY: number; maxY: number } {
  return { minX: 1 - MAP_MARGIN - width, maxX: map.width - 1 + MAP_MARGIN, minY: 1 - MAP_MARGIN - height, maxY: map.height - 1 + MAP_MARGIN };
}

/** L'objet posé en (x, y) touche-t-il la carte ou sa marge (voir objectRange) ? */
export function objectInMargin(map: Pick<MapLayout, 'width' | 'height'>, element: Pick<MapElement, 'width' | 'height'>, x: number, y: number): boolean {
  const { minX, maxX, minY, maxY } = objectRange(map, element);
  return x >= minX && x <= maxX && y >= minY && y <= maxY;
}

/** Clé d'une case hors de la carte (index négatif ou qui déborde : pas d'index de case). */
export const outerKey = (x: number, y: number): string => `${x},${y}`;

/**
 * Calques d'objets d'une carte : un objet d'un calque est dessiné par-dessus ceux des calques inférieurs (un pot sur une
 * table), à ligne égale quand ils sont triés avec les personnages. Collisions et interactions ne dépendent pas du calque.
 */
export const OBJECT_LAYER_COUNT = 3;

/** Objets dans l'ordre où les dessiner : calque par calque, puis dans l'ordre de pose. */
export const inDrawingOrder = <T extends Pick<MapObject, 'layer'>>(objects: readonly T[]): T[] => [...objects].sort((a, b) => a.layer - b.layer);

/** Étendue dessinée d'un objet posé, en cases de la carte : ses cases, débordements de ses calques compris, une fois tourné. */
export function drawnBounds(object: Pick<MapObject, 'x' | 'y' | 'turns'>, element: Pick<MapElement, 'width' | 'height' | 'gridSize' | 'layers'>): { left: number; top: number; right: number; bottom: number } {
  const g = element.gridSize;
  let left = 0, top = 0, right = element.width, bottom = element.height;
  for (const layer of element.layers) {
    left = Math.min(left, layer.offsetX / g);
    top = Math.min(top, layer.offsetY / g);
    right = Math.max(right, (layer.offsetX + layer.width) / g);
    bottom = Math.max(bottom, (layer.offsetY + layer.height) / g);
  }
  const corners = [{ x: left, y: top }, { x: right, y: top }, { x: left, y: bottom }, { x: right, y: bottom }].map((corner) => turnedPoint(object.turns, element, corner));
  return {
    left: object.x + Math.min(...corners.map((corner) => corner.x)), top: object.y + Math.min(...corners.map((corner) => corner.y)),
    right: object.x + Math.max(...corners.map((corner) => corner.x)), bottom: object.y + Math.max(...corners.map((corner) => corner.y)),
  };
}

/** Un objet à trier avec les personnages : sa case (sa position du moment, pour un objet mobile), son calque et son élément. */
export interface SortableObject {
  x: number;
  y: number;
  layer: number;
  turns: Turns;
  element: Pick<MapElement, 'width' | 'height' | 'gridSize' | 'layers'>;
}

/**
 * Ligne de tri de chaque objet avec les personnages (dans l'ordre de `objects`) : sa ligne du bas, repoussée au moins
 * jusqu'à celle des objets des calques inférieurs qu'il recouvre à l'écran. Ainsi, un objet d'un calque du dessus est
 * toujours dessiné par-dessus ceux d'en dessous qu'il chevauche, même placé plus haut sur la carte ; à ligne égale, le
 * calque départage.
 */
export function sortRows(objects: readonly SortableObject[]): number[] {
  const rows = objects.map((object) => object.y + object.element.height - 1);
  const bounds = objects.map((object) => drawnBounds(object, object.element));
  // Calque par calque, du bas vers le haut : un objet du calque 2 tient compte des lignes déjà repoussées du calque 1.
  const order = [...objects.keys()].sort((a, b) => objects[a]!.layer - objects[b]!.layer);
  for (const i of order) {
    const box = bounds[i]!;
    for (const j of order) {
      if (objects[j]!.layer >= objects[i]!.layer) break;
      const other = bounds[j]!;
      const overlaps = box.left < other.right && other.left < box.right && box.top < other.bottom && other.top < box.bottom;
      if (overlaps && rows[j]! > rows[i]!) rows[i] = rows[j]!;
    }
  }
  return rows;
}

/** Un objet posé : son coin supérieur gauche est sur la case (x, y). */
export interface MapObject {
  elementId: number;
  x: number;
  y: number;
  /** Calque d'objets (0 à OBJECT_LAYER_COUNT - 1). */
  layer: number;
  turns: Turns;
  /**
   * Chemin suivi par un objet mobile (élément `moving`), depuis sa case de pose, ou par un véhicule pendant un voyage ;
   * null pour un objet immobile.
   */
  motion: ObjectMotion | null;
  /** Voyage d'un véhicule (élément `vehicle`) ; null pour les autres objets. */
  trip: ObjectTrip | null;
  /** Texte lu par le joueur (élément à interaction « Lecture » : pancarte…) ; vide pour les autres. */
  text: string;
  /** Ce que donne l'objet (élément à interaction « Drop » : coffre, buisson…) ; null pour les autres. Jamais envoyé aux joueurs. */
  drop: ObjectDrop | null;
  /** Objet du sac à y utiliser (élément à interaction « Utiliser objet ») ; null pour les autres. */
  use: ObjectUse | null;
  /** Articles en vente, dans l'ordre (élément à interaction « Boutique » : comptoir…) ; null pour les autres. */
  shop: ItemId[] | null;
  /** Présence selon une quête du joueur (null : toujours présent). */
  presence: ObjectPresence | null;
  /**
   * Mécanisme (interrupteur, ou objet commandé par des courants ; voir mechanisms.ts) ; null : aucun. Ses calques joués
   * « Au déclenchement » suivent alors son état, pas les personnages proches.
   */
  mechanism: ObjectMechanism | null;
}

/**
 * Objet posé qui dépend d'une quête, pour chaque joueur : présent seulement une fois la quête terminée (`completed`
 * vrai), ou présent jusqu'à ce qu'elle le soit (il disparaît alors). Avec `step`, c'est l'arrivée à cette étape de la
 * quête (à partir de 0 ; ou une fois la quête terminée) qui le fait apparaître ou disparaître, plutôt que sa fin.
 */
export interface ObjectPresence {
  questId: number;
  completed: boolean;
  step: number | null;
}

/** L'objet est-il là pour ce joueur, d'après où il en est dans ses quêtes ? */
export function isObjectPresent(presence: ObjectPresence | null, progress: QuestProgress): boolean {
  if (presence === null) return true;
  const reached = presence.step === null ? progress.get(presence.questId)?.status === 'completed' : reachedStep(presence.questId, presence.step, progress);
  return reached === presence.completed;
}

/**
 * Objet posé sur lequel le joueur utilise un objet de son sac (une Capsule…) : c'est un objectif de quête (voir
 * `use_item`), qui le vise par son identifiant. Hors de cette quête, ce n'est qu'un décor.
 */
export interface ObjectUse {
  /** Identifiant stable, unique dans la carte (l'objectif de quête s'y rattache). */
  id: string;
  /** Objet demandé, retiré du sac à l'utilisation (null : pas encore choisi). */
  item: ItemId | null;
}

/** Objet posé visé par un objectif « interagir avec un objet » : son élément et la case de son coin. */
export interface ObjectSpot {
  elementId: number;
  x: number;
  y: number;
}

/** Clé d'un objet posé sur sa carte (`MapView.inspectable`). */
export const objectSpotKey = ({ elementId, x, y }: ObjectSpot): string => `${elementId}:${x}:${y}`;

/**
 * L'objet posé (élément à « Interaction possible », sans autre interaction) a un message, affiché quand on l'examine
 * hors quête ; sans message, on ne l'examine que pendant une quête qui le demande.
 */
export const hasInspectMessage = (object: Pick<MapObject, 'text'>, element: Pick<MapElement, 'inspectable' | 'interaction'>): boolean =>
  element.inspectable && element.interaction === null && object.text.trim() !== '';

export const OBJECT_USE_ID_PATTERN = /^[a-z0-9-]{4,40}$/;

/** Nouveau réglage « Utiliser objet » (identifiant unique en pratique : horodatage et tirage). */
export const newObjectUse = (item: ItemId | null): ObjectUse => ({ id: `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`, item });

/** Longueur maximale du texte d'un objet à lire. */
export const OBJECT_TEXT_MAX = 1000;

/** Chemin que suit vraiment l'objet posé : son élément est mobile et le chemin a au moins une étape. */
export function motionOf(object: MapObject, element: Pick<MapElement, 'moving'>): ObjectMotion | null {
  return element.moving && object.motion?.points.length ? object.motion : null;
}

/** Centres des lumières d'un élément, en cases depuis son coin supérieur gauche (voir `lightCells`). */
export function lightSpots({ width, height, lightCells }: Pick<MapElement, 'width' | 'height' | 'lightCells'>): { x: number; y: number }[] {
  const spots: { x: number; y: number }[] = [];
  const seen = new Set<number>();
  for (let start = 0; start < width * height; start++) {
    if (!lightCells[start] || seen.has(start)) continue;
    // Groupe de cases cochées voisines (haut, bas, gauche, droite) : une seule lumière, en son centre.
    let sumX = 0, sumY = 0, count = 0;
    const queue = [start];
    seen.add(start);
    while (queue.length) {
      const cell = queue.pop() ?? 0;
      const x = cell % width, y = Math.floor(cell / width);
      sumX += x + 0.5;
      sumY += y + 0.5;
      count++;
      for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) {
        const next = ny * width + nx;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height || !lightCells[next] || seen.has(next)) continue;
        seen.add(next);
        queue.push(next);
      }
    }
    spots.push({ x: sumX / count, y: sumY / count });
  }
  return spots;
}

export const ELEMENT_LIMITS = { maxSide: 16, maxLayers: 6, maxVariants: 32, maxFrames: 64, minFrameMs: 20, maxFrameMs: 5000, gridSizes: [8, 16, 24, 32, 48, 64] } as const;
export const DEFAULT_FRAME_MS = 200;
export const MAX_OBJECTS_PER_MAP = 5000;
/** Tilesets téléversés dans l'administration (onglet Éléments). */
export const TILESET_URL = '/uploads/tilesets/';
/** Terrain qui remplit les nouvelles cartes : il ne peut pas être supprimé. */
export const DEFAULT_TERRAIN_ID = 0;
/**
 * Calques de terrain d'une carte : le sol (calque 0, chaque case a un terrain) puis les calques du dessus, dessinés
 * dans l'ordre par-dessus (des feuilles sur un chemin…), où une case peut rester vide.
 */
export const TERRAIN_LAYER_COUNT = 4;
/** Case vide d'un calque de terrain du dessus. */
export const EMPTY_TILE = -1;

/** Calques du dessus d'une nouvelle carte de `cells` cases : tous vides. */
export const emptyUpperTiles = (cells: number): number[][] =>
  Array.from({ length: TERRAIN_LAYER_COUNT - 1 }, () => new Array<number>(cells).fill(EMPTY_TILE));

/** Terrains de la case d'index `index`, du sol vers le haut (cases vides des calques du dessus omises). */
export function terrainsAt(map: Pick<MapLayout, 'tiles' | 'upperTiles'>, index: number): number[] {
  const ids = [map.tiles[index] ?? EMPTY_TILE];
  for (const layer of map.upperTiles) {
    const id = layer[index] ?? EMPTY_TILE;
    if (id !== EMPTY_TILE) ids.push(id);
  }
  return ids;
}

/** Chaque terrain posé sur la carte, tous calques confondus (sol d'abord), avec l'index de sa case. */
function forEachTerrain(map: Pick<MapLayout, 'tiles' | 'upperTiles'>, visit: (id: number, index: number, layer: number) => void): void {
  map.tiles.forEach((id, index) => visit(id, index, 0));
  map.upperTiles.forEach((tiles, k) => tiles.forEach((id, index) => { if (id !== EMPTY_TILE) visit(id, index, k + 1); }));
}

/**
 * Cases de la carte couvertes par un objet posé, avec l'index de la case de l'élément qui s'y trouve une fois l'objet
 * tourné (un objet tourné garde ses cases : voir turnStep).
 */
export function* coveredCells(object: Pick<MapObject, 'x' | 'y' | 'turns'>, element: Pick<MapElement, 'width' | 'height'>): Generator<{ x: number; y: number; cell: number }> {
  for (let dy = 0; dy < element.height; dy++) {
    for (let dx = 0; dx < element.width; dx++) yield { x: object.x + dx, y: object.y + dy, cell: turnedCell(object.turns, element, dx, dy) };
  }
}

/** Index de la case de l'élément qui arrive en (dx, dy) de ses cases une fois tourné de `turns` quarts de tour. */
function turnedCell(turns: Turns, { width, height }: Pick<MapElement, 'width' | 'height'>, dx: number, dy: number): number {
  if (isMirrored(turns)) return dy * width + (width - 1 - dx);
  let x = dx, y = dy, w = width, h = height;
  // Un quart de tour défait à chaque fois : la case (x, y) venait de (y, w - 1 - x).
  for (let k = 0; k < turns; k++) [x, y, w, h] = [y, w - 1 - x, h, w];
  return y * w + x;
}

/**
 * Point (en cases depuis le coin de l'élément) une fois l'élément tourné de `turns` quarts de tour autour de son centre
 * (retourné gauche-droite en miroir, voir isMirrored).
 */
export function turnedPoint(turns: Turns, { width, height }: Pick<MapElement, 'width' | 'height'>, point: { x: number; y: number }): { x: number; y: number } {
  if (isMirrored(turns)) return { x: width - point.x, y: point.y };
  let { x, y } = point, w = width, h = height;
  for (let k = 0; k < turns; k++) [x, y, w, h] = [h - y, x, h, w];
  return { x, y };
}

/** Groupe de raccord présent sur une case : celui d'un terrain et son calque, ou celui d'un objet (`layer` null). */
export interface CellGroup {
  group: string;
  layer: number | null;
}

/**
 * Groupes d'une case vus par un terrain du calque `layer` : ceux de ce calque et des objets (les terrains des autres
 * calques ne s'y raccordent pas) ; sans calque (objet), tous.
 */
export function groupsSeen(groups: readonly CellGroup[], layer?: number): string[] {
  return groups.flatMap((entry) => (layer === undefined || entry.layer === null || entry.layer === layer ? [entry.group] : []));
}

/** Ce qu'une carte sait de ses cases, une fois ses éléments connus. */
export interface CellIndex {
  /** Cases bloquées (terrain bloquant ou hitbox d'un objet). */
  blocked: ReadonlySet<number>;
  interactions: ReadonlyMap<number, Interaction>;
  /** Cases dont le terrain masque le bas des personnages. */
  covering: ReadonlySet<number>;
  /** Groupes de raccord présents sur chaque case (terrain de chaque calque et objets ; voir groupsSeen). */
  groups: ReadonlyMap<number, readonly CellGroup[]>;
  /** Groupes de raccord des cases de la marge (voir outerKey), pour les raccords au bord de la carte. */
  outerGroups: ReadonlyMap<string, readonly CellGroup[]>;
  /** Sens de chaque case de bordure (un de ses terrains en a un ; voir hasBorder), pour choisir son image. */
  borders: ReadonlyMap<number, BorderDirection>;
  /** Bordures à sauter : sens dans lequel on saute par-dessus la case (elle est bloquée dans les autres cas). */
  ledges: ReadonlyMap<number, BorderDirection>;
  /** Cases où l'on surfe (terrain le plus haut bloquant et surfable, sans objet qui bloque). */
  surf: ReadonlySet<number>;
}

export interface MapLayout {
  width: number;
  height: number;
  /** Calque 0, le sol : un terrain par case. */
  tiles: readonly number[];
  /** Calques de terrain du dessus (calque 1, 2…) : un terrain par case, ou EMPTY_TILE. */
  upperTiles: readonly (readonly number[])[];
  objects: readonly MapObject[];
  borders: readonly MapBorder[];
  rotations: readonly MapRotation[];
  /** Terrain peint dans la marge (absent : rien). */
  outerTiles?: readonly MapOuterTile[];
}

/** `flipped` : objets posés dont la hitbox est inversée (mécanisme actif, voir mechanisms.ts). */
export function indexCells(map: MapLayout, elements: ReadonlyMap<number, MapElement>, flipped?: ReadonlySet<MapObject>): CellIndex {
  const blocked = new Set<number>();
  const interactions = new Map<number, Interaction>();
  const covering = new Set<number>();
  const groups = new Map<number, CellGroup[]>();
  const addGroup = (index: number, group: CellGroup) => {
    const list = groups.get(index);
    if (list) list.push(group); else groups.set(index, [group]);
  };
  // La marge ne compte que pour les raccords : ni collision, ni interaction.
  const outerGroups = new Map<string, CellGroup[]>();
  const addOuterGroup = (x: number, y: number, group: CellGroup) => {
    const key = outerKey(x, y);
    const list = outerGroups.get(key);
    if (list) list.push(group); else outerGroups.set(key, [group]);
  };
  for (const { x, y, layer, tileId } of map.outerTiles ?? []) {
    const element = elements.get(tileId);
    if (element) addOuterGroup(x, y, { group: element.group, layer });
  }
  // Raccords, interactions et hautes herbes viennent de tous les calques ; la hitbox, du terrain le plus haut (une
  // échelle sans hitbox posée sur un mur rend la case praticable).
  const top = topTerrains(map);
  forEachTerrain(map, (id, index, layer) => {
    const element = elements.get(id);
    if (!element) return;
    addGroup(index, { group: element.group, layer });
    if (element.interaction) interactions.set(index, element.interaction);
    if (element.coversCharacters) covering.add(index);
  });
  const surf = new Set<number>();
  top.forEach((id, index) => {
    const element = elements.get(id);
    if (!element || element.collision[0]) blocked.add(index); // terrain inconnu : prudence
    if (element?.collision[0] && element.obstacle === 'surf') surf.add(index);
  });
  for (const object of map.objects) {
    const element = elements.get(object.elementId);
    // Un objet qui se déplace est un décor : ni collision, ni interaction, ni raccord avec ses voisins.
    if (!element || motionOf(object, element)) continue;
    for (const { x, y, cell } of coveredCells(object, element)) {
      if (x < 0 || y < 0 || x >= map.width || y >= map.height) {
        addOuterGroup(x, y, { group: element.group, layer: null });
        continue;
      }
      const index = y * map.width + x;
      addGroup(index, { group: element.group, layer: null });
      if (!element.collision[cell] !== (flipped?.has(object) ?? false)) continue;
      blocked.add(index);
      surf.delete(index); // un rocher dans l'eau : on ne passe pas
      if (element.interaction) interactions.set(index, element.interaction);
    }
  }
  const borders = indexBorders(map, elements);
  return { blocked, interactions, covering, groups, outerGroups, borders, ledges: indexLedges(top, elements, borders, blocked), surf };
}

/** Terrain le plus haut de chaque case (le dernier calque qui n'y est pas vide). */
function topTerrains(map: Pick<MapLayout, 'tiles' | 'upperTiles'>): Map<number, number> {
  const top = new Map<number, number>();
  forEachTerrain(map, (id, index) => top.set(index, id));
  return top;
}

/** Sens de chaque case dont un terrain (de n'importe quel calque) est une bordure. */
function indexBorders(map: MapLayout, elements: ReadonlyMap<number, MapElement>): Map<number, BorderDirection> {
  const chosen = new Map(map.borders.map(({ x, y, dir }) => [y * map.width + x, dir]));
  const borders = new Map<number, BorderDirection>();
  forEachTerrain(map, (id, index) => {
    const element = elements.get(id);
    if (element && hasBorder(element)) borders.set(index, chosen.get(index) ?? DEFAULT_BORDER_DIRECTION);
  });
  return borders;
}

/**
 * Sens de saut de chaque case dont le terrain le plus haut est une bordure à sauter ; ces cases sont ajoutées à
 * `blocked` (on ne fait que les sauter).
 */
function indexLedges(top: ReadonlyMap<number, number>, elements: ReadonlyMap<number, MapElement>, borders: ReadonlyMap<number, BorderDirection>,
  blocked: Set<number>): Map<number, BorderDirection> {
  const ledges = new Map<number, BorderDirection>();
  top.forEach((id, index) => {
    if (!elements.get(id)?.ledge) return;
    blocked.add(index);
    ledges.set(index, borders.get(index) ?? DEFAULT_BORDER_DIRECTION);
  });
  return ledges;
}

/**
 * Case où atterrit un personnage qui, depuis `from`, avance vers `dir` sur une bordure à sauter dans ce sens ;
 * null s'il n'y a pas de bordure à sauter devant lui (la case d'arrivée reste à vérifier : libre, dans la carte).
 */
export function ledgeLanding(cells: Pick<CellIndex, 'ledges'>, width: number, from: Position, dir: Direction): Position | null {
  const [dx, dy] = DIRECTION_VECTORS[dir];
  const x = from.x + dx, y = from.y + dy;
  if (x < 0 || y < 0 || x >= width) return null;
  const ledge = cells.ledges.get(y * width + x);
  return ledge && borderSides(ledge).includes(dir) ? { x: x + dx, y: y + dy } : null;
}

/**
 * Image d'une case de bordure : celle de son sens, ou pour un angle dont les voisins vers ses deux sens sont
 * des bordures, celle du coin intérieur (le coin extérieur a ses bordures voisines de l'autre côté).
 */
export function borderSprite(borders: ReadonlyMap<number, BorderDirection>, width: number, x: number, y: number): BorderSprite | null {
  // Hors de la carte (marge), pas de sens enregistré : l'index retomberait sur une autre case.
  if (x < 0 || x >= width) return null;
  const dir = borders.get(y * width + x);
  if (!dir) return null;
  const sides = borderSides(dir);
  if (sides.length === 1) return dir;
  const inner = sides.every((side) => {
    const [dx, dy] = DIRECTION_VECTORS[side];
    const nx = x + dx;
    return nx >= 0 && nx < width && borders.has((y + dy) * width + nx);
  });
  return inner ? `inner-${dir as BorderCorner}` : dir;
}

/** Objet posé qui couvre la case (x, y) et se voit dessus : du calque le plus haut, puis le plus récent ; `layer` : sur ce calque seulement. */
export function objectAt(objects: readonly MapObject[], elements: ReadonlyMap<number, MapElement>, x: number, y: number, layer?: number): MapObject | undefined {
  return inDrawingOrder(objects).findLast((object) => {
    const element = elements.get(object.elementId);
    return element !== undefined && (layer === undefined || object.layer === layer)
      && x >= object.x && y >= object.y && x < object.x + element.width && y < object.y + element.height;
  });
}

/**
 * Le voisin porteur de `groups` (voir groupsSeen) se raccorde-t-il à l'élément ? Un voisin qui porte le même groupe
 * (ou un groupe associé) se raccorde toujours, même s'il porte aussi un groupe bordé (un objet posé dessus).
 */
export function connects(element: Pick<MapElement, 'group' | 'connectsWith' | 'bordersOnly'>, groups: readonly string[] | null): boolean {
  if (groups === null) return true; // hors de la carte : on prolonge
  if (groups.some((group) => group === element.group || element.connectsWith.includes(group))) return true;
  return element.bordersOnly.length > 0 && !groups.some((group) => element.bordersOnly.includes(group));
}

/** Morceau d'un raccord en ligne, d'après ses voisins raccordés avant (au-dessus, à gauche) et après (en dessous, à droite). */
export function stripPiece(before: boolean, after: boolean): StripPiece {
  return before ? (after ? 'middle' : 'end') : after ? 'start' : 'single';
}

/** Morceau de barrière d'une case, d'après ses voisins raccordés au nord, à l'est, au sud et à l'ouest. */
export function linePiece(n: boolean, e: boolean, s: boolean, w: boolean): 'single' | 'horizontal' | 'vertical' | LineCorner | 'junction' {
  const horizontal = e || w, vertical = n || s;
  if (!horizontal && !vertical) return 'single';
  if (!vertical) return 'horizontal';
  if (!horizontal) return 'vertical';
  if (n !== s && e !== w) return `${s ? 'n' : 's'}${e ? 'w' : 'e'}`;
  return 'junction';
}

/** Pièce de blob utilisée pour un quart de case, selon ses deux voisins orthogonaux et le diagonal. */
export function blobPiece(horizontal: boolean, vertical: boolean, diagonal: boolean, sx: -1 | 1, sy: -1 | 1): BlobPiece {
  const v = sy < 0 ? 'n' : 's', h = sx < 0 ? 'w' : 'e';
  if (!horizontal && !vertical) return `${v}${h}`;
  if (horizontal && !vertical) return v;
  if (!horizontal && vertical) return h;
  return diagonal ? 'center' : `i${v}${h}`;
}

/** Quarts d'une case, dans l'ordre : nord-ouest, nord-est, sud-ouest, sud-est. */
export const BLOB_QUARTERS = [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const;

/** Pièces du bloc 3×3 (bords, coins, centre) : chacune peut couvrir une case entière. */
const BLOB_TILES = ['nw', 'n', 'ne', 'w', 'center', 'e', 'sw', 's', 'se'] as const satisfies readonly BlobPiece[];

/**
 * Pièce du bloc 3×3 dont l'image entière convient à la case, d'après les pièces de ses quatre quarts (dans l'ordre
 * de BLOB_QUARTERS) ; null s'il n'y en a pas (bande d'une case de large, coin intérieur…). Dessiner l'image entière
 * garde les raccords que le dessin fait déborder d'un quart (un coin arrondi sur toute la case, par exemple).
 */
export function blobTile(quarters: readonly BlobPiece[]): BlobPiece | null {
  return BLOB_TILES.find((tile) => BLOB_QUARTERS.every(([sx, sy], i) => {
    const edgeH = tile.endsWith(sx < 0 ? 'w' : 'e'), edgeV = tile.startsWith(sy < 0 ? 'n' : 's');
    return quarters[i] === blobPiece(!edgeH, !edgeV, true, sx, sy);
  })) ?? null;
}
